import { lazy } from 'react'

// sessionStorage key holding the timestamp of the last chunk-triggered reload.
// A timestamp (rather than a boolean) lets us reload again for a *future* stale
// deploy while still bounding reloads to at most one per cooldown window — so a
// genuinely broken deploy can never trap the user in a reload loop.
const RELOAD_AT_KEY = 'coformit:chunk-reload-at'
const DEFAULT_COOLDOWN_MS = 10000

/**
 * True when an error looks like a failed dynamic import — i.e. the hashed chunk
 * referenced by an already-loaded session was deleted by a subsequent deploy
 * (#533). Covers Vite/Rollup and webpack-style messages.
 */
export function isChunkLoadError(err) {
  if (!err) return false
  if (err.name === 'ChunkLoadError') return true
  const msg = String(err.message || '')
  return (
    /dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /Loading chunk \d+ failed/i.test(msg) ||
    /Loading CSS chunk/i.test(msg)
  )
}

/**
 * Runs a dynamic import and, if it fails because the chunk is gone (stale
 * deploy), reloads the page once so a fresh (no-cache) index.html can supply the
 * current chunk names. Exported for unit testing; deps are injectable.
 */
export async function importWithReload(importFn, {
  storage = window.sessionStorage,
  reload = () => window.location.reload(),
  now = () => Date.now(),
  cooldownMs = DEFAULT_COOLDOWN_MS,
} = {}) {
  try {
    return await importFn()
  } catch (err) {
    if (!isChunkLoadError(err)) throw err

    const lastReloadAt = Number(storage.getItem(RELOAD_AT_KEY) || 0)
    if (now() - lastReloadAt > cooldownMs) {
      storage.setItem(RELOAD_AT_KEY, String(now()))
      reload()
      // Keep the Suspense fallback on screen until the reload replaces the page.
      return new Promise(() => {})
    }

    // We already reloaded very recently and the chunk still won't load — stop
    // reloading and let the ChunkErrorBoundary show a manual-retry prompt.
    throw err
  }
}

/**
 * Drop-in replacement for React.lazy that auto-recovers from post-deploy stale
 * chunk errors (#533).
 */
export function lazyWithReload(importFn) {
  return lazy(() => importWithReload(importFn))
}
